import React from 'react';

import { Currency } from 'components/common/DataFormat';
import { dataCell, rightAlignedHeader, stackedGroupHeader } from './reportTableCells';

/**
 * Metrics shared by the channel, daily, employee and monthly sales reports. All
 * four read the same figures off the API payload verbatim — there is no mapping
 * layer — so the field names here must match the reporting API exactly.
 */
export type SalesReportMetrics = {
  numberOfServices?: number;
  serviceGrossTotal?: number;
  numberOfProducts?: number;
  productGrossTotal?: number;
  numberOfVouchers?: number;
  voucherGrossTotal?: number;
  paidBySalonVoucher?: number;
  grossTotalLeftover?: number;
};

type SalesReportHeaderLabels = {
  /** Label for the leading, non-numeric column (e.g. "Date", "Employee"). */
  firstColumn: string;
  services: string;
  products: string;
  vouchers: string;
  prepaid: string;
  total: string;
  quantity: string;
  sales: string;
};

/**
 * The two-row header shared by all four reports. Both rows describe the same nine
 * columns:
 *
 *     label | services x2 | products x2 | vouchers x2 | prepaid | total
 *
 * `ReportTable` has no `rowSpan` support and every header row is 44px, so each
 * column that logically spans both rows needs an empty placeholder cell in the
 * second row — without them the second row is short and every qty/sales label
 * shifts left into the wrong column; the `height: 'double'` values are a
 * deprecated no-op kept only so existing callers still type-check.
 */
export const salesReportHeaderRows = ({
  firstColumn,
  services,
  products,
  vouchers,
  prepaid,
  total,
  quantity,
  sales,
}: SalesReportHeaderLabels) => {
  const spanningColumnPlaceholder = () => ({
    content: '',
    align: 'right' as const,
    width: 'narrow' as const,
  });

  return [
    [
      { content: firstColumn, width: 'wide' as const, height: 'double' as const },
      stackedGroupHeader(services),
      stackedGroupHeader(products),
      stackedGroupHeader(vouchers),
      {
        content: prepaid,
        align: 'right' as const,
        width: 'narrow' as const,
        height: 'double' as const,
      },
      {
        content: total,
        align: 'right' as const,
        width: 'narrow' as const,
        height: 'double' as const,
      },
    ],
    [
      // Sits under the leading label column, which spans both rows.
      { content: '' },
      rightAlignedHeader(quantity),
      rightAlignedHeader(sales),
      rightAlignedHeader(quantity),
      rightAlignedHeader(sales),
      rightAlignedHeader(quantity),
      rightAlignedHeader(sales),
      // Sit under the prepaid and grand total columns, which span both rows.
      spanningColumnPlaceholder(),
      spanningColumnPlaceholder(),
    ],
  ];
};

/**
 * `data-testid`s for the trailing cells. These differ per report and are asserted
 * by the Cypress e2e specs, so they are passed in rather than derived.
 */
type TrailingCellTestIds = {
  voucherQuantity: string;
  voucherGross: string;
  prepaid: string;
  total: string;
};

/**
 * The trailing cells shared by all four reports — voucher qty/gross, the amount
 * paid by salon voucher, and the remaining total. Keeping these in one place means
 * `paidBySalonVoucher` and `grossTotalLeftover` are read in exactly one place.
 */
export const trailingSalesCells = (row: SalesReportMetrics, testIds: TrailingCellTestIds) => [
  dataCell(row.numberOfVouchers ?? 0, { dataTestId: testIds.voucherQuantity }),
  dataCell(<Currency amount={row.voucherGrossTotal} />, { dataTestId: testIds.voucherGross }),
  dataCell(<Currency amount={row.paidBySalonVoucher ?? 0} />, { dataTestId: testIds.prepaid }),
  dataCell(<Currency amount={row.grossTotalLeftover ?? 0} />, {
    variant: 'grandTotal',
    dataTestId: testIds.total,
  }),
];
